import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useStoreRooms } from '@/entities/room'

import { Room } from '@/shared/types/store'
import { fromPServedUser } from '@/shared/utils/uri'

import { getOrCreateRoomForSession } from '../lib/get-or-create-room-for-session'

type SessionRoomBinding = {
  number: string
  roomId?: string
}

export const useCallSessionRoomStore = defineStore('call-session-room', () => {
  const sessionBindings = ref(new Map<string, SessionRoomBinding>())

  const linkSessionToRoom = (sessionId: string, room: Room) => {
    sessionBindings.value.set(sessionId, {
      number: room.number,
      roomId: room.id,
    })
  }

  const bindSession = (sessionId: string, number: string, displayName?: string): Room => {
    const room = getOrCreateRoomForSession(number, displayName)

    linkSessionToRoom(sessionId, room)

    return room
  }

  const unbindSession = (sessionId: string) => {
    sessionBindings.value.delete(sessionId)
  }

  const linkRoomToSessions = (room: Room) => {
    const roomNumber = fromPServedUser(room?.pServedUser ?? '')
    for (const [sessionId, binding] of sessionBindings.value) {
      if (binding.number === roomNumber || binding.number === room.number) {
        sessionBindings.value.set(sessionId, {
          ...binding,
          roomId: room.id,
        })
      }
    }
  }

  const getRoom = (sessionId: string): Room => {
    const storeRooms = useStoreRooms()
    const binding = sessionBindings.value.get(sessionId)

    if (binding?.roomId) {
      const room = storeRooms.getRoomById(binding.roomId)
      if (room) {
        return room
      }
    }

    const number = binding?.number

    const existing = Array.from(storeRooms.rooms.values()).find(x => x.number === number)
    if (existing) {
      sessionBindings.value.set(sessionId, {
        number: number as string,
        roomId: existing.id,
      })
      return existing
    }

    return getOrCreateRoomForSession(number as string)
  }

  const findSessionIdByRoomPServed = (pServedUser: string): string | undefined => {
    const roomNumber = fromPServedUser(pServedUser)
    for (const [sessionId] of sessionBindings.value) {
      const room = getRoom(sessionId)
      if (room?.pServedUser === pServedUser || room?.number === roomNumber) {
        return sessionId
      }
    }
    return undefined
  }

  return {
    bindSession,
    unbindSession,
    linkSessionToRoom,
    linkRoomToSessions,
    getRoom,
    findSessionIdByRoomPServed,
  }
})
